import './LandingPage.scss';
import {capitalizeFirstLetter} from '../../utils/TextAlterations';
import {daysAgo} from '../../utils/DateFunctions';
import Card from 'react-bootstrap/Card';
import {Button, message, Modal, Tag} from 'antd';
import {DollarSign, MapPin, Globe, Clock, Building, Laptop, Heart, Timer, BriefcaseBusiness} from 'lucide-react';

import ApplyJobButton from '../../components/ApplyJob/ApplyJobButton';
import {permColors} from '../../utils/colors';
import {formatCurrency} from '../../utils/currency';
import {ROUTES} from '../../utils/ROUTES';
import {CopyOutlined, LinkOutlined} from '@ant-design/icons';
import {capitalize} from '@mui/material';
import {Link} from 'react-router-dom';

interface JobContentProps {
	job: any;
	onNavigate?: (path: string) => void;
	authenticatedButtons: any;
	isModal?: boolean;
}

export const descriptionStyle = ' text-sm [&>ul]:list-disc [&>ul]:text-sm [&>ol]:list-decimal [&>ol]:ml-6 [&>ul]:ml-6 [&>p]:text-sm [&>p]:my-3 text-charcoal ';

function JobContent({job, onNavigate, authenticatedButtons, isModal = false}: JobContentProps) {
	return (
		<>
			<div className="flex flex-col gap-1 bg-backgroundModern shadow-border-chill-background p-4 rounded-lg">
				<div className="flex items-center justify-between gap-2 mb-4">
					<div className="rounded-xl border border-gray-200 bg-white p-1 w-11 h-11 flex items-center justify-center">
						{!job?.created_by_image ? (
							<BriefcaseBusiness className="w-5 h-5" strokeWidth={1.5} />
						) : (
							<img src={job?.created_by_image} alt="profile" className="w-full h-full object-cover rounded-lg" />
						)}
					</div>
					<div className="flex flex-row items-end gap-2">
						{!isModal && (
							<Button
								className="rounded-md min-h-fit text-sm"
								onClick={() => {
									navigator.clipboard.writeText(window.location.origin + ROUTES.JOB_PAGE.replace(':jobId', job.id));
									message.success('Job URL copied to clipboard', 2);
								}}
								icon={<CopyOutlined className="text-sm" />}>
								Copy URL
							</Button>
						)}
						{authenticatedButtons(job)}
						<ApplyJobButton job={job} />
					</div>
				</div>

				<p className="text-sm sm:text-base font-medium flex items-center gap-2" style={{color: permColors.charcoal}}>
					{job?.company_name && capitalizeFirstLetter(job.company_name)}
				</p>

				<div className="flex items-center gap-2">
					<Link
						className="text-xl sm:text-2xl font-medium mb-2 flex items-center gap-2 hover:underline cursor-pointer w-fit"
						style={{color: permColors.darkCharcoal}}
						to={`${ROUTES.JOB_PAGE.replace(':jobId', job.id)}`}>
						{capitalizeFirstLetter(job.title)}
						<LinkOutlined className="text-lg" />
					</Link>
					<span className="text-sm font-normal" style={{color: permColors.grey}}>
						{job.created_at !== null ? `Posted ${daysAgo(job.created_at)} day${daysAgo(job.created_at) === 1 ? '' : 's'} ago` : ''}
					</span>
				</div>

				<div className="flex items-center flex-wrap gap-2">
					{job?.engagement_type && <Tag className="text-sm m-0 shadow-border-chill-background bg-white text-charcoal border-none">{job.engagement_type}</Tag>}
					{job?.category && <Tag className="text-sm m-0 shadow-border-chill-background bg-white text-charcoal border-none">{job.category}</Tag>}
					{job?.job_type && <Tag className="text-sm m-0 shadow-border-chill-background bg-white text-charcoal border-none">{job.job_type}</Tag>}
				</div>
			</div>

			<div className="p-4">
				<div className="mb-6">
					<h3 className="text-base sm:text-lg font-medium text-charcoal mb-4">Job Overview</h3>
					<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
						{/* Salary */}
						{job?.payment_rate_from && job?.show_salary_on_job && (
							<div className="flex items-start gap-3">
								<div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 bg-white shadow-border-no-background rounded-xl flex items-center justify-center">
									<DollarSign className="w-4 h-4  text-charcoal" />
								</div>
								<div>
									<p className="text-xs sm:text-sm font-medium text-charcoal">Salary</p>
									<p className="text-xs sm:text-sm text-charcoal/70">
										{formatCurrency(job?.payment_rate_from, job?.payment_rate_to, job?.payment_rate_type, job?.payment_rate_currency)}
									</p>
								</div>
							</div>
						)}

						{/* Company Location */}
						<div className="flex items-start gap-3">
							<div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 bg-white shadow-border-no-background rounded-xl flex items-center justify-center">
								<MapPin className="w-4 h-4  text-charcoal" />
							</div>
							<div>
								<p className="text-xs sm:text-sm font-medium text-charcoal">Company Location</p>
								<p className="text-xs sm:text-sm text-charcoal/70">{job.country}</p>
							</div>
						</div>

						{/* Role Location Type */}
						{job?.role_location_type && (
							<div className="flex items-start gap-3">
								<div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 bg-white shadow-border-no-background rounded-xl flex items-center justify-center">
									<Globe className="w-4 h-4  text-charcoal" />
								</div>
								<div>
									<p className="text-xs sm:text-sm font-medium text-charcoal">Work Type</p>
									<p className="text-xs sm:text-sm text-charcoal/70 capitalize">
										{job.role_location_type} {job.role_location_type.toLowerCase() === 'remote' && '(Philippines)'}
									</p>
								</div>
							</div>
						)}

						{/* Role Location */}
						{job?.role_location_type !== 'remote' && job?.role_location && (
							<div className="flex items-start gap-3">
								<div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 bg-white shadow-border-no-background rounded-xl flex items-center justify-center">
									<MapPin className="w-4 h-4  text-charcoal" />
								</div>
								<div>
									<p className="text-xs sm:text-sm font-medium text-charcoal">Role Location</p>
									<p className="text-xs sm:text-sm text-charcoal/70 capitalize">{job.role_location}</p>
								</div>
							</div>
						)}

						{/* Timezone */}
						{job?.work_schedule_timezone && (job?.work_schedule_type === 'fixed' || job?.work_schedule_type === 'shift-based') && (
							<div className="flex items-start gap-3">
								<div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 bg-white shadow-border-no-background rounded-xl flex items-center justify-center">
									<Timer className="w-4 h-4  text-charcoal" />
								</div>
								<div>
									<p className="text-xs sm:text-sm font-medium text-charcoal">Timezone</p>
									<p className="text-xs sm:text-sm text-charcoal/70">{job.work_schedule_timezone}</p>
								</div>
							</div>
						)}

						{/* Schedule Type */}
						{job?.work_schedule_type && (
							<div className="flex items-start gap-3">
								<div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 bg-white shadow-border-no-background rounded-xl flex items-center justify-center">
									<Building className="w-4 h-4  text-charcoal" />
								</div>
								<div>
									<p className="text-xs sm:text-sm font-medium text-charcoal">Schedule Type</p>
									<p className="text-xs sm:text-sm text-charcoal/70 capitalize">{job.work_schedule_type}</p>
								</div>
							</div>
						)}
					</div>

					{/* Working Hours & Days */}
					{job?.work_schedule_type === 'fixed' && job?.work_schedule_hours_start && (
						<div className="mt-6 pt-6 border-t border-neutral-200/60">
							<div className="flex items-start gap-3">
								<div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 bg-white shadow-border-no-background rounded-xl flex items-center justify-center">
									<Clock className="w-4 h-4  text-charcoal" />
								</div>
								<div className="flex-1">
									<p className="text-xs sm:text-sm font-medium text-charcoal">Working Schedule</p>
									<div>
										<p className="text-xs sm:text-sm text-charcoal/70">
											<span className="font-medium">Hours:</span> {job.work_schedule_hours_start} - {job.work_schedule_hours_end}
										</p>
										{job?.work_schedule_days && (
											<p className="text-xs sm:text-sm text-charcoal/70">
												<span className="font-medium">Days:</span>{' '}
												{job.work_schedule_days
													.split(',')
													.map((day: any) => capitalize(day))
													.join(', ')}
											</p>
										)}
									</div>
								</div>
							</div>
						</div>
					)}

					{/* Shifts */}
					{job?.shifts?.length > 0 && (
						<div className="mt-6 pt-6 border-t border-neutral-200/60">
							<div className="flex items-start gap-3">
								<div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 bg-white shadow-border-no-background rounded-xl flex items-center justify-center">
									<Clock className="w-4 h-4  text-charcoal" />
								</div>
								<div className="flex-1">
									<p className="text-xs sm:text-sm font-medium text-charcoal">Available Shifts</p>
									<div>
										{job.shifts.map((shift: any, index: any) => (
											<div key={index} className="flex justify-between items-center py-2 px-3 bg-neutral-50/70 rounded-md">
												<span className="text-xs sm:text-sm font-medium text-charcoal">{shift.day_of_week}</span>
												<span className="text-xs sm:text-sm text-charcoal/70">
													{shift.start_time} - {shift.end_time}
												</span>
											</div>
										))}
									</div>
								</div>
							</div>
						</div>
					)}

					{/* Benefits & Equipment */}
					{(job?.equipment_allocation || job?.provide_HMO) && (
						<div className="mt-6 pt-6 border-t border-neutral-200/60">
							<div className="flex items-start gap-3">
								<div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 bg-white shadow-border-no-background rounded-xl flex items-center justify-center">
									<Laptop className="w-4 h-4  text-charcoal" />
								</div>
								<div className="flex-1">
									<p className="text-xs sm:text-sm font-medium text-charcoal">Benefits & Equipment</p>
									<div>
										{job?.equipment_allocation && (
											<p className="text-xs sm:text-sm text-charcoal/70">
												<span className="font-medium">Equipment:</span> <span className="capitalize">{job.equipment_allocation}</span>
											</p>
										)}
										{job?.provide_HMO && (
											<p className="text-xs sm:text-sm text-charcoal/70">
												<span className="font-medium">Health Benefits:</span> HMO Provided
											</p>
										)}
									</div>
								</div>
							</div>
						</div>
					)}
				</div>

				<div className="pb-2">
					<p className="text-base underline font-semibold mt-4 mb-2" style={{color: permColors.darkCharcoal}}>
						Description
					</p>
					<div
						className={`grow ${descriptionStyle} ${isModal ? ' p-3 ' : ' min-h-[200px] '}`}
						dangerouslySetInnerHTML={{
							__html: job?.description,
						}}
					/>
				</div>
			</div>
		</>
	);
}

interface SelectedJobCardProps {
	job: any;
	onNavigate: (path: string) => void;
	authenticatedButtons: any;
}

export function SelectedJobCard({job, onNavigate, authenticatedButtons}: SelectedJobCardProps) {
	return (
		<div className="selectedFeed ">
			<Card className=" rounded-xl p-3 flex flex-col  gap-1 border-none shadow-border" style={{height: '95vh', overflowY: 'auto'}}>
				<JobContent job={job} onNavigate={onNavigate} authenticatedButtons={authenticatedButtons} />
			</Card>
		</div>
	);
}

interface JobModalProps {
	job: any;
	show: boolean;
	onHide: any;
	onNavigate: (path: string) => void;
	authenticatedButtons: any;
}

export function JobModal({job, show, onHide, onNavigate, authenticatedButtons}: JobModalProps) {
	return (
		<Modal open={show} onCancel={onHide} footer={null} width={700} centered styles={{body: {padding: '0 !important'}, content: {padding: '20px 6px'}}} className="p-1">
			<div className="p-0 pt-6 flex flex-col justify-between gap-1">
				<JobContent job={job} onNavigate={onNavigate} authenticatedButtons={authenticatedButtons} isModal={true} />
			</div>
		</Modal>
	);
}
